/**
 * tree.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

/**
 * Maximum number of levels to allow tree operations upon.
 * @type {number}
 */
const DEPTH_LIMIT : number = 1000;

/**
 * {@link Tree} provides very basic tree operations, where the definition of
 * "very basic" is "the ones I needed at the time". The {@link Tree} class
 * is otherwise just a wrapper around the generic {@link TreeNode} in its root.
 */
export class Tree<T>
{
  public root : TreeNode<T> | null = null;

  /**
   * Create a {@link Tree} from its root. At present we don't use null nodes,
   * though we can have an empty tree with no root node at all.
   * @param  {TreeNode<T>|T} root
   * @return {Tree<T>}
   */
  constructor (root? : TreeNode<T> | T)
  {
    if (root !== undefined)
    {
      this.root = (root instanceof TreeNode) ? root : new TreeNode(root);
    }
  }

  /**
   * Whether the tree has a root node, which would indicate it wasn't empty
   * @return {boolean}
   */
  public isEmpty () : boolean
  {
    return this.root === null;
  }

  /**
   * Run a callback on the tree depth-first, quitting when we scan the entire
   * tree or when the callback returns true.
   * @param  {TreeNode<any>|Tree<any>} start
   * @param  {Function} fn
   */
  // tslint:disable-next-line:cyclomatic-complexity
  public static depthFirst<T> (
    start : TreeNode<T> | Tree<T>,
    previsit? : (node : TreeNode<T>) => any,
    visit? : (node : TreeNode<T>) => any,
    postvisit? : (node : TreeNode<T>) => any
  ) : void
  {
    if (start instanceof Tree)
    {
      // We want the root to be a TreeNode, so unwrap Trees
      if (start.isEmpty())
      {
        return;
      }
      start = start.root;
    }
    previsit  = previsit  || (() => null);
    visit     = visit     || (() => null);
    postvisit = postvisit || (() => null);

    enum CheckPhase
    {
      Previsited,
      LeftVisited,
      SelfVisited,
      RightVisited
      // no need for a PostVisited because we just pop it off the stack
    }

    let quit : boolean;
    quit = previsit(start);

    // The stack of nodes we still need to revisit. The phase indicates what
    // operations have already been completed on that node.
    const revisit : Array<[TreeNode<any>, CheckPhase]> =
      [[start, CheckPhase.Previsited]];

    while (revisit.length > 0)
    {
      if (quit === true)
      {
        return;
      }
      const [parent, phase] : [TreeNode<any>, CheckPhase] = revisit.shift();
      switch (phase)
      {
        case CheckPhase.Previsited:
          revisit.unshift([parent, CheckPhase.LeftVisited]);
          if (parent.hasLeft())
          {
            quit = previsit(parent.left);
            revisit.unshift([parent.left, CheckPhase.Previsited]);
          }
          break;
        case CheckPhase.LeftVisited:
          quit = visit(parent);
          revisit.unshift([parent, CheckPhase.SelfVisited]);
          break;
        case CheckPhase.SelfVisited:
          revisit.unshift([parent, CheckPhase.RightVisited]);
          if (parent.hasRight())
          {
            quit = previsit(parent.right);
            revisit.unshift([parent.right, CheckPhase.Previsited]);
          }
          break;
        case CheckPhase.RightVisited:
          quit = postvisit(parent);
          break;
          // do NOT re-insert the node since we're done with it!
        default:
          throw new Error("Unknown visitor state: " + phase + "!");
      }
    }
  }

  /**
   * If an item exists in the tree, compared using the equals function, if
   * provided, or generic triple-equals otherwise.
   * @param  {T}      item
   * @param  {T}      equals
   * @return {[type]}
   */
  public has (item : T, equals? : (a : T, b : T) => boolean) : boolean
  {
    if (!equals)
    {
      equals = (a : T, b : T) => a === b;
    }
    let found : boolean = false;
    Tree.depthFirst<T>(
      this,
      (node : TreeNode<T>) => {
        found = equals(item, node.value);
        return found;
      }
    );
    return found;
  }

  /**
   * Get the tree as an array in LSR order (i.e. the sorted order for a BST)
   * @return {T[]}
   */
  public list () : T[]
  {
    const out : T[] = [];
    Tree.depthFirst<T>(
      this,
      null,
      (node : TreeNode<T>) => out.push(node.value)
    );
    return out;
  }

  /**
   * Get the tree as an array in pre-visit SLR order
   * @return {T[]}
   */
  public depthFirstList () : T[]
  {
    const out : T[] = [];
    Tree.depthFirst<T>(this, (node : TreeNode<T>) => out.push(node.value));
    return out;
  }

  /**
   * Insert a node into the tree using a provided comparator function. This
   * method doesn't assume a particular tree structure per se, so be careful
   * if you try to insert an object into an unsorted tree (or a tree with a
   * different arrangement than your comparator.) Strictly speaking the
   * comparator determines if the new node belongs to the right of a branch.
   * @param  {TreeNode<T>|T}  nodeOrValue [description]
   * @param  {TreeNode<T>}    rightOf [description]
   */
  public insert (
    nodeOrValue : TreeNode<T> | T,
    rightOf? : (node : TreeNode<T>, comparison : TreeNode<T>) => boolean
  ) : void
  {
    const insertion : TreeNode<T> = (nodeOrValue instanceof TreeNode)
      ? nodeOrValue
      : new TreeNode(nodeOrValue);

    if (this.isEmpty())
    {
      this.root = insertion;
      return;
    }

    if (!rightOf)
    {
      rightOf = (node : TreeNode<T>, comparison : TreeNode<T>) =>
        comparison.value < node.value;
    }

    let prev : TreeNode<T> = this.root;
    let i : number;
    for (i = 0; i < DEPTH_LIMIT; i++)
    {
      if (rightOf(insertion, prev))
      {
        if (prev.hasRight())
        {
          prev = prev.right;
        }
        else
        {
          prev.right = insertion;
          return;
        }
      }
      else
      {
        if (prev.hasLeft())
        {
          prev = prev.left;
        }
        else
        {
          prev.left = insertion;
          return;
        }
      }
    }

    if (i === DEPTH_LIMIT)
    {
      throw new Error("Could not insert element due to tree depth limit");
    }
  }

  /**
   * Generate a trivial "stick" from a list by simply appending successive
   * elements as their predecessor's child on a particular side.
   * @param  {E[]}      list
   * @param  {boolean}  left If true, attach as left children, else right
   * @return {Tree<E>}
   */
  public static stickFromList<E> (list : E[], left : boolean = true) : Tree<E>
  {
    if (list.length < 1)
    {
      throw new Error("Cannot tree-ify empty list");
    }
    let parent : TreeNode<E> = new TreeNode(list[0]);
    const tree : Tree<E> = new Tree(parent);
    list.slice(1).forEach((item : any) => {
      if (left)
      {
        parent.left = new TreeNode(item);
        parent = parent.left;
      }
      else
      {
        parent.right = new TreeNode(item);
        parent = parent.right;
      }
    });
    return tree;
  }
}

/**
 * The nodes within a binary-branching {@link Tree}
 */
export class TreeNode<T>
{
  /**
   * Create a node with the given value and children, if any
   * @param  {T}                value
   * @param  {TreeNode<T>|null} left
   * @param  {TreeNode<T>|null} right
   * @return {TreeNode<T>}
   */
  constructor (
    public value : T,
    public left : TreeNode<T> | null = null,
    public right : TreeNode<T> | null = null
  )
  {
    // Nothing
  }

  /**
   * Whether this node has a child on the left side
   * @return {boolean}
   */
  public hasLeft () : boolean
  {
    return this.left !== null;
  }

  /**
   * Whether this node has a child on the right side
   * @return {boolean}
   */
  public hasRight () : boolean
  {
    return this.right !== null;
  }

  /**
   * Whether this node has no children
   * @return {boolean}
   */
  public isLeaf () : boolean
  {
    return !this.hasLeft() && !this.hasRight();
  }

  /**
   * A list of the values of this node and its descendants, only visiting left
   * children and returning them in visitation order. (No, I don't remember why
   * I thought I needed this.)
   * @return {T[]}
   */
  public leftDescendantValues () : T[]
  {
    const descendants : T[] = [];
    Tree.depthFirst(this, (node : TreeNode<T>) => {
      descendants.push(node.value);
      return !node.hasLeft();
    });
    return descendants;

  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/utilities/tree.ts